import {useState, useRef, useCallback} from 'react'
import {getImageDataFromCanvas} from '../../../../features/custom-shapes/fragment-detection'
import {getCustomShapeWorker} from '../../../../webworkers/helpers/customShape'
import {FRAGMENT_DETECTION} from '../../../../constant/fragmentDetection'

const DETECTION_TIMEOUT_MS = 30000

const defaultOptions = {
    minRegionSize: FRAGMENT_DETECTION.MIN_REGION_SIZE,
    minWarningSize: FRAGMENT_DETECTION.MIN_WARNING_SIZE,
    minThickness: FRAGMENT_DETECTION.MIN_THICKNESS,
    alphaThreshold: FRAGMENT_DETECTION.ALPHA_THRESHOLD,
    maxCanvasSize: FRAGMENT_DETECTION.MAX_CANVAS_SIZE,
    method: 'auto',
    dpi: null,
}

/**
 * Hook: fragment detection.
 * Input: detect options.
 * Returns: { fragments, runDetection, isDetecting, error }.
 * runDetection(canvas) must be called with a canvas (e.g. from Konva layer.toCanvas()).
 */
export function useFragmentDetection(detectOptions = {}) {
    const options = {...defaultOptions, ...detectOptions}
    const [fragments, setFragments] = useState([])
    const [isDetecting, setIsDetecting] = useState(false)
    const [error, setError] = useState(null)
    const detectionIdRef = useRef(0)

    const runDetection = useCallback(
        async (canvas) => {
            if (!canvas || options.enableDetection === false) {
                if (options.enableDetection === false) {
                    setIsDetecting(false)
                    setFragments([])
                }
                return
            }

            const currentId = ++detectionIdRef.current
            setIsDetecting(true)
            setError(null)

            try {
                const imageData = getImageDataFromCanvas(canvas)
                if (currentId !== detectionIdRef.current) return

                const worker = await getCustomShapeWorker()
                if (currentId !== detectionIdRef.current) return

                const detectionPromise = worker.detectFragments(imageData, {
                    minRegionSize: options.minRegionSize,
                    minWarningSize: options.minWarningSize,
                    minThickness: options.minThickness,
                    alphaThreshold: options.alphaThreshold,
                    maxCanvasSize: options.maxCanvasSize,
                    method: options.method,
                    dpi: options.dpi,
                })
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('Fragment detection timeout')), DETECTION_TIMEOUT_MS)
                })

                const detectedFragments = await Promise.race([detectionPromise, timeoutPromise])

                if (currentId !== detectionIdRef.current) return
                setFragments(detectedFragments)
                setError(null)
            } catch (err) {
                if (currentId === detectionIdRef.current) {
                    setError(err)
                    setFragments([])
                    console.error('Fragment detection failed:', err)
                }
            } finally {
                if (currentId === detectionIdRef.current) {
                    setIsDetecting(false)
                }
            }
        },
        [
            options.enableDetection,
            options.minRegionSize,
            options.minWarningSize,
            options.minThickness,
            options.alphaThreshold,
            options.maxCanvasSize,
            options.method,
            options.dpi,
        ],
    )

    const clearFragments = useCallback(() => {
        setFragments([])
        setError(null)
    }, [])

    return {fragments, runDetection, isDetecting, error, clearFragments}
}
